import { useEffect, useRef, useState, type FormEvent } from "react";
import type { FieldDef } from "./fields";

type Props = {
  fields: FieldDef[];
  initial: Record<string, unknown>;
  onSave: (values: Record<string, unknown>) => Promise<unknown>;
  submitLabel: string;
  /** Options for select fields whose choices come from the API (category, department, parent). */
  selectOptions?: Record<string, { value: string; label: string }[]>;
  /** Send every field (create) instead of only the changed ones (update). */
  sendAll?: boolean;
};

const norm = (v: unknown) => {
  if (typeof v === "string") { const t = v.trim(); return t === "" ? null : t; }
  return v === undefined ? null : v;
};

export function RecordForm({ fields, initial, onSave, submitLabel, selectOptions = {}, sendAll = false }: Props) {
  const fromInitial = () => Object.fromEntries(fields.map((f) => [f.key, initial[f.key] ?? ""]));
  const [values, setValues] = useState<Record<string, unknown>>(fromInitial);
  const dirty = useRef(false);
  // New data from the server replaces the form only when the user has no unsaved edits.
  useEffect(() => { if (!dirty.current) setValues(fromInitial()); }, [JSON.stringify(initial)]); // eslint-disable-line react-hooks/exhaustive-deps
  const [state, setState] = useState<{ busy: boolean; error: string; saved: boolean }>({ busy: false, error: "", saved: false });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const out = Object.fromEntries(fields
      .filter((f) => sendAll || norm(values[f.key]) !== norm(initial[f.key]))
      .map((f) => [f.key, norm(values[f.key])]));
    if (!Object.keys(out).length) { dirty.current = false; setState({ busy: false, error: "", saved: true }); return; }
    setState({ busy: true, error: "", saved: false });
    try { await onSave(out); dirty.current = false; setState({ busy: false, error: "", saved: true }); }
    catch (err) { setState({ busy: false, error: (err as Error).message, saved: false }); }
  };

  return (
    <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
      {fields.map((f) => {
        const id = `f-${f.key}`;
        const common = { id, required: f.required, value: String(values[f.key] ?? ""),
          onChange: (e: { target: { value: string } }) => { dirty.current = true; setValues((v) => ({ ...v, [f.key]: e.target.value })); setState((s) => ({ ...s, saved: false })); } };
        const opts = f.options ?? selectOptions[f.key] ?? [];
        return (
          <div key={f.key} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
            <label className="label" htmlFor={id}>{f.label}</label>
            {f.type === "textarea" ? <textarea className="input min-h-20" {...common} />
              : f.type === "select" ? (
                <select className="input" {...common}>
                  <option value="">—</option>
                  {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : <input className="input" type={f.type === "url" ? "url" : "text"} placeholder={f.type === "url" ? "https://" : undefined} {...common} />}
          </div>
        );
      })}
      <div className="flex items-center gap-3 sm:col-span-2">
        <button className="btn-primary" disabled={state.busy}>{submitLabel}</button>
        {state.saved && <span className="text-sm text-emerald-700">Saved</span>}
        {state.error && <span role="alert" className="text-sm text-red-600">{state.error}</span>}
      </div>
    </form>
  );
}
